import { useToast } from '@/hooks/use-toast';
import { FastingProtocol } from '@/types/fasting';
import { FastingSession } from './FastingHistoryContext';

type FastingActionsProps = {
  setIsActive: (value: boolean) => void;
  setIsPaused: (value: boolean) => void;
  setTimeElapsed: (value: number | ((prev: number) => number)) => void;
  setStartTime: (value: number | null) => void;
  setPausedTime: (value: number | null) => void;
  setEndTime: (value: number | null) => void;
  setWaterIntake: (value: number | ((prev: number) => number)) => void;
  setSelectedProtocol: (protocol: FastingProtocol) => void;
  setTotalPauseDuration: (value: number | ((prev: number) => number)) => void;
  isActive: boolean;
  isPaused: boolean;
  startTime: number | null;
  pausedTime: number | null;
  endTime: number | null;
  timeElapsed: number;
  totalPauseDuration: number;
  selectedProtocol: FastingProtocol;
  fastingHistory: any;
}

export const useFastingActions = ({
  setIsActive,
  setIsPaused,
  setTimeElapsed,
  setStartTime,
  setPausedTime,
  setEndTime,
  setWaterIntake,
  setSelectedProtocol,
  setTotalPauseDuration,
  isActive,
  isPaused,
  startTime,
  pausedTime,
  endTime,
  timeElapsed,
  totalPauseDuration,
  selectedProtocol,
  fastingHistory
}: FastingActionsProps) => {
  const { toast } = useToast();

  const startFast = () => {
    const now = Date.now();
    setIsActive(true);
    setIsPaused(false);
    setStartTime(now);
    // Calculate and set the end time based on protocol duration
    const calculatedEndTime = now + (selectedProtocol.durationHours * 60 * 60 * 1000);
    setEndTime(calculatedEndTime);
    setTimeElapsed(0);
    setWaterIntake(0);
    // Reset pause tracking
    setPausedTime(null);
    setTotalPauseDuration(0);
    toast({
      title: "Fast Started",
      description: `You've started a ${selectedProtocol.name} fast. Good luck!`,
    });
  };
  
  const pauseFast = () => {
    setIsPaused(true);
    setPausedTime(Date.now());
    toast({
      title: "Fast Paused",
      description: "Your fast has been paused. Resume when you're ready.",
      variant: "default",
    });
  };
  
  const resumeFast = () => {
    if (pausedTime) {
      // We don't adjust start/end time anymore - we track accumulated pause time instead
      const pauseDuration = Date.now() - pausedTime;
      setTotalPauseDuration(prev => prev + pauseDuration);
    }
    
    setIsPaused(false);
    setPausedTime(null);
    toast({
      title: "Fast Resumed",
      description: "Keep going! You're doing great.",
    });
  };
  
  const stopFast = () => {
    // Save incomplete fast to history
    if (fastingHistory && startTime && timeElapsed > 60) { // Only save if more than a minute has passed
      const now = Date.now();
      const session: FastingSession = {
        id: `fast-${now}`,
        startTime: startTime,
        endTime: now,
        duration: timeElapsed,
        targetDuration: selectedProtocol.durationHours * 60 * 60,
        completed: false,
        protocolId: selectedProtocol.id,
        protocolName: selectedProtocol.name,
        date: new Date().toISOString().split('T')[0]
      };
      fastingHistory.addSession(session);
    }
    
    setIsActive(false);
    setIsPaused(false);
    setTimeElapsed(0);
    setStartTime(null);
    setPausedTime(null);
    setEndTime(null);
    setWaterIntake(0);
    setTotalPauseDuration(0);
    // Clear localStorage when stopping
    localStorage.removeItem('fastingState');
    toast({
      title: "Fast Stopped",
      description: "Your fast has been stopped. Start again when you're ready.",
      variant: "destructive",
    });
  };
  
  const selectProtocol = (protocol: FastingProtocol) => {
    setSelectedProtocol(protocol);
    if (isActive) {
      stopFast();
    } else {
      setTimeElapsed(0);
    }
  };
  
  const increaseWaterIntake = () => {
    setWaterIntake(prev => prev + 1);
    toast({
      title: "Water Intake Updated",
      description: "Great job staying hydrated!",
    });
  };
  
  const decreaseWaterIntake = () => {
    setWaterIntake(prev => Math.max(0, prev - 1));
  };

  return {
    startFast,
    pauseFast,
    resumeFast,
    stopFast,
    selectProtocol,
    increaseWaterIntake,
    decreaseWaterIntake
  };
};
